/**
 * Post-authentication redirect validation.
 *
 * The auth pages accept a `redirectTo` query parameter so a deep link survives
 * sign-in, and www legitimately forwards to the app subdomain. The previous
 * check was `value.startsWith("http")`, which is a transport test, not an origin
 * test: `?redirectTo=https://lyder-talk-verify.example` sent a freshly
 * authenticated user straight to an attacker's site from a trusted sign-in page.
 *
 * This module resolves the value against an allowlist of first-party origins and
 * returns the app's own root for anything else.
 */
import { getAppUrl, getAdminUrl, getRootUrl } from "./subdomain";

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/** Hosts this deployment may send an authenticated user to. */
function firstPartyHosts(): Set<string> {
  const hosts = new Set<string>();

  const add = (url: string) => {
    const host = hostOf(url);
    if (host) hosts.add(host);
  };

  if (typeof window !== "undefined") {
    add(window.location.origin);
  }

  add(getAppUrl("/"));
  add(getAdminUrl("/"));
  add(getRootUrl("/"));

  return hosts;
}

function isFirstPartyHost(hostname: string, allowed: Set<string>): boolean {
  if (allowed.has(hostname)) return true;

  // Local development: localhost and *.localhost on any port.
  if (hostname === "localhost" || hostname.endsWith(".localhost")) return true;

  // First-party product domains, including preview deployments under them.
  if (hostname === "lydertalk.com" || hostname.endsWith(".lydertalk.com")) return true;

  return false;
}

/**
 * Returns a redirect target that is safe to navigate to.
 *
 * The result is either a root-relative path (`/x`) or an absolute first-party
 * URL. Callers should compare against `/` to decide between router navigation
 * and a full page load.
 */
export function resolveSafeRedirect(
  raw: string | null | undefined,
  fallback: string = "/",
): string {
  if (!raw) return fallback;

  let value = raw.trim();
  if (!value) return fallback;

  // Decode once; a double-encoded value is not a legitimate deep link and is
  // re-validated after decoding.
  try {
    value = decodeURIComponent(value);
  } catch {
    return fallback;
  }

  value = value.trim();
  if (!value) return fallback;

  // Protocol-relative ("//host") is an absolute URL in disguise.
  if (value.startsWith("//") || value.startsWith("/\\")) return fallback;

  // Root-relative paths are same-origin by construction.
  if (value.startsWith("/")) return value;

  // Anything without a scheme is a relative path; force it under the root so it
  // cannot be resolved against an unexpected base.
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value)) {
    return `/` + value.replace(/^\.?\//, "");
  }

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return fallback;
  }

  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return fallback;

  const allowed = firstPartyHosts();
  if (!isFirstPartyHost(parsed.hostname.toLowerCase(), allowed)) return fallback;

  return parsed.toString();
}

/** True when the resolved target needs a full page load rather than router navigation. */
export function isAbsoluteRedirect(target: string): boolean {
  return /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(target);
}
